/**
 * Shared constants for investor information forms
 * Extracted from InvestorProfile.tsx
 *
 * CONVENTION: dropdown `value`s are always stored UPPERCASE so the value
 * persisted to the DB matches the UI state on round-trip without any
 * case-normalisation layer. Labels stay in readable case for display.
 * Numeric-only values (marital-status codes, income-bracket ids) stay as
 * their original codes — they're not language tokens.
 */

/**
 * Nomination: relationship options — Laravel's seven specific relations, in the
 * order and Title-Case values it stores (`nominee_relation1`). Laravel only
 * uppercases them visually via CSS `text-transform`, so the persisted token stays
 * Title Case. Deliberately NOT generic categories: Father/Mother and Son/Daughter
 * are distinct values there, and there is no "Other".
 */
export const NOMINATION_RELATIONSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'Father', label: 'Father' },
  { value: 'Mother', label: 'Mother' },
  { value: 'Brother', label: 'Brother' },
  { value: 'Sister', label: 'Sister' },
  { value: 'Spouse', label: 'Spouse' },
  { value: 'Daughter', label: 'Daughter' },
  { value: 'Son', label: 'Son' },
];

// Nominee/guardian document types are INVESTOR-TYPE dependent (user decision 2026-09-04):
// NRI + OCI get Laravel's exact pair (values 'PAN' / 'Passport' — Title-case Passport
// matches what Laravel stored, so PDFs/Dataverse comparisons line up); RI gets
// Aadhaar + Other (no Laravel precedent — the PHP flow was NRI/OCI-centric).
export const NOMINATION_DOC_TYPE_OPTIONS_NRI_OCI: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'PAN', label: 'PAN' },
  { value: 'Passport', label: 'Passport' },
];

export const NOMINATION_DOC_TYPE_OPTIONS_RI: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'AADHAAR', label: 'Aadhaar' },
  { value: 'OTHER', label: 'Other' },
];

/** Aligned with `information-update.blade.php` Section8 (Other Information). */
// Parity fix: `value` must match Laravel's stored strings exactly (Title Case, correct band amounts),
// otherwise the same answer stores a different value than Laravel/Dataverse. Labels are display-only.
export const OTHER_SOURCE_OF_FUNDS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'Salary', label: 'Salary' },
  { value: 'Business Income', label: 'Business Income' },
  { value: 'Investment Income', label: 'Investment Income' },
  { value: 'Agriculture', label: 'Agriculture' },
  { value: 'Others', label: 'Others' },
];

export const OTHER_EDUCATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'Under Graduate', label: 'Under-Graduate' },
  { value: 'Graduate', label: 'Graduate' },
  { value: 'Post Graduate', label: 'Post Graduate' },
  { value: 'Others', label: 'Others' },
];

export const OTHER_GROSS_INCOME_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: '100000', label: 'Below 1 lakh' },
  { value: '500000', label: '1 to 5 lakhs' },
  { value: '1000000', label: '5 to 10 lakhs' },
  { value: '2000000', label: '10 to 25 lakhs' },
  { value: '2700000', label: 'Above 25 lakhs' },
];

export const OTHER_NET_WORTH_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: '2500000', label: 'Less than 50 lakhs' },
  { value: '7500000', label: '50 - 100 lakhs' },
  { value: '10000000', label: 'Above 100 lakhs' },
];

export const OTHER_OCCUPATION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'Salaried - Pvt Sector', label: 'Salaried - Pvt Sector' },
  { value: 'Salaried - Public Sector', label: 'Salaried - Public Sector' },
  { value: 'Salaried - GOVT Service', label: 'Salaried - GOVT Service' },
  { value: 'Student', label: 'Student' },
  { value: 'Business', label: 'Business' },
  { value: 'Professional', label: 'Professional' },
  { value: 'Agriculturist', label: 'Agriculturist' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Housewife', label: 'Housewife' },
  { value: 'Others', label: 'Others' },
];

export const OTHER_INVESTMENT_EXPERIENCE_YEARS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select' },
  { value: 'Less than 2 years', label: 'Less than 2 years' },
  { value: '2-5 year', label: '2-5 years' },
  { value: 'More than 5 year', label: 'More than 5 years' },
];

export const OTHER_INVESTMENT_EXPERIENCE_IN: readonly string[] = ['Equity', 'Debt', 'Derivative', 'Commodities', 'Others'];
